import { defineStore } from "pinia";
import { ref } from "vue";
import { getSubscriptions, fetchSubscription, saveSubscription, editSubscription, pauseSubscriptionAction, resumeSubscriptionAction, cancelSubscriptionAction, fetchSubscriptionDonations, } from "../infrastructure/adapters/subscriptionService";
export const useSubscriptionStore = defineStore("subscription", () => {
    const subscriptions = ref([]);
    const currentSubscription = ref(null);
    const subscriptionDonations = ref([]);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const error = ref(null);
    const totalItems = ref(0);
    const totalPages = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(20);
    async function loadSubscriptions(params) {
        isLoading.value = true;
        error.value = null;
        try {
            const result = await getSubscriptions({
                donorId: params?.donorId,
                status: params?.status,
                frequency: params?.frequency,
                page: params?.page ?? currentPage.value,
                pageSize: params?.pageSize ?? pageSize.value,
                sortBy: params?.sortBy,
                sortOrder: params?.sortOrder,
            });
            subscriptions.value = result.result;
            totalItems.value = result.totalItems;
            totalPages.value = result.totalPages;
            currentPage.value = result.currentPage;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.loadFailed";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function loadSubscription(subscriptionId) {
        isLoading.value = true;
        error.value = null;
        try {
            currentSubscription.value = await fetchSubscription(subscriptionId);
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.loadFailed";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function createSubscription(payload) {
        isSaving.value = true;
        error.value = null;
        try {
            return await saveSubscription(payload);
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.saveFailed";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function updateSubscription(subscriptionId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await editSubscription(subscriptionId, payload);
            currentSubscription.value = subscription;
            return subscription;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.saveFailed";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function pauseSubscription(subscriptionId) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await pauseSubscriptionAction(subscriptionId);
            _updateInList(subscription);
            if (currentSubscription.value?.id === subscriptionId) {
                currentSubscription.value = subscription;
            }
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.actionFailed";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function resumeSubscription(subscriptionId) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await resumeSubscriptionAction(subscriptionId);
            _updateInList(subscription);
            if (currentSubscription.value?.id === subscriptionId) {
                currentSubscription.value = subscription;
            }
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.actionFailed";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function cancelSubscription(subscriptionId) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await cancelSubscriptionAction(subscriptionId);
            _updateInList(subscription);
            if (currentSubscription.value?.id === subscriptionId) {
                currentSubscription.value = subscription;
            }
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.actionFailed";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function loadSubscriptionDonations(subscriptionId, options) {
        isLoading.value = true;
        error.value = null;
        try {
            const result = await fetchSubscriptionDonations(subscriptionId, options);
            subscriptionDonations.value = result.result;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.loadFailed";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    function _updateInList(subscription) {
        const idx = subscriptions.value.findIndex((s) => s.id === subscription.id);
        if (idx !== -1) {
            subscriptions.value[idx] = subscription;
        }
    }
    function clearCurrentSubscription() {
        currentSubscription.value = null;
        subscriptionDonations.value = [];
    }
    return {
        subscriptions,
        currentSubscription,
        subscriptionDonations,
        isLoading,
        isSaving,
        error,
        totalItems,
        totalPages,
        currentPage,
        pageSize,
        loadSubscriptions,
        loadSubscription,
        createSubscription,
        updateSubscription,
        pauseSubscription,
        resumeSubscription,
        cancelSubscription,
        loadSubscriptionDonations,
        clearCurrentSubscription,
    };
});
